import { useContext, useEffect, useState } from 'react';

import { Close, Menu as MenuIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { SpecialistContext } from 'context';
import { SCREEN_MIN_WIDTH } from 'hooks/useComplaintDevice/useComplaintDevice';
import useOnInit from 'hooks/useOnInit';
import { getChatStatus } from 'services/specialist/specialist';

import LogoContainer from 'components/LogoContainer/LogoContainer';
import MenuItem from 'components/MenuItem';

import { MenuId } from '..';

import { items } from './items';

import { MenuItemNav } from './types';

import * as S from './styles';

const Menu: React.FC = () => {
  const navigate = useNavigate();

  const { specialistInfo } = useContext(SpecialistContext);

  const [menuItems, setMenuItems] = useState<MenuItemNav[]>(items);
  const [mobileMenuIsOpen, setMobileMenuIsOpen] = useState(false);

  const handleOpenMobileMenu = () => {
    setMobileMenuIsOpen(true);
  };

  const handleCloseMenu = () => {
    setMobileMenuIsOpen(false);
  };

  const updateNotificationItemMenu = (key: string, value: boolean) => {
    const index = menuItems.findIndex(item => item.key === key);
    menuItems[index].hasNotification = value;

    setMenuItems([...menuItems]);
  };

  async function checkIfChatHaveUnreadMsg() {
    const chatStatus = await getChatStatus();
    const hasUnreadMessages = chatStatus.some(item => item.has_unread_message);

    updateNotificationItemMenu('chat', hasUnreadMessages);
  }

  useOnInit(() => {
    checkIfChatHaveUnreadMsg();
  });

  const handleClickMenuItem = (key: string) => {
    if (key === 'chat') {
      updateNotificationItemMenu(key, false);
    }

    handleCloseMenu();
  };

  const getUserNameOrOccupationBy = (
    name: string | undefined,
    by: 'name' | 'occupation',
  ) => {
    if (by === 'name') {
      return name && name.split('-')[0];
    }

    return name && name.split('-')[1];
  };

  const handleSignOut = () => {
    navigate('/signout');
  };

  const handleWindowSizeChange = () => {
    if (window.innerWidth >= SCREEN_MIN_WIDTH) {
      setMobileMenuIsOpen(false);
    }
  };

  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, []);

  return (
    <S.Container data-testid="sidebar-menu">
      <S.CollapseButton onClick={handleOpenMobileMenu}>
        <MenuIcon />
      </S.CollapseButton>
      <LogoContainer />
      <S.ItemsMenu>
        {menuItems.map(item => (
          <MenuItem
            itemKey={item.key}
            key={item.key}
            baseUrls={item.baseUrls}
            redirectUrl={item.redirectUrl}
            title={item.title}
            Icon={item.icon}
            IconSelected={item.iconSelected}
            totalNotification={item.totalNotification}
            onClick={handleClickMenuItem}
            hasNotification={item.hasNotification}
            allowedRoles={item.allowedRoles}
            allowedGeoCodes={item.allowedGeoCodes}
          />
        ))}
      </S.ItemsMenu>
      <S.Footer>
        <MenuId
          user={{
            fullName: getUserNameOrOccupationBy(
              specialistInfo?.fullName,
              'name',
            ),
            occupation: getUserNameOrOccupationBy(
              specialistInfo?.fullName,
              'occupation',
            ),
          }}
          avatarUrl={specialistInfo?.avatar && specialistInfo?.avatar}
          onSignOut={handleSignOut}
        />
      </S.Footer>
      {mobileMenuIsOpen && (
        <S.MenuDrawer
          open={mobileMenuIsOpen}
          anchor="left"
          onBackdropClick={handleCloseMenu}
        >
          <S.MobileMenu>
            <S.ItemsMenu>
              {menuItems.map(item => (
                <MenuItem
                  itemKey={item.key}
                  key={item.key}
                  baseUrls={item.baseUrls}
                  redirectUrl={item.redirectUrl}
                  title={item.title}
                  Icon={item.icon}
                  IconSelected={item.iconSelected}
                  totalNotification={item.totalNotification}
                  onClick={handleClickMenuItem}
                  hasNotification={item.hasNotification}
                  allowedRoles={item.allowedRoles}
                  allowedGeoCodes={item.allowedGeoCodes}
                />
              ))}
            </S.ItemsMenu>
            <S.Footer>
              <MenuId
                user={{
                  fullName: getUserNameOrOccupationBy(
                    specialistInfo?.fullName,
                    'name',
                  ),
                  occupation: getUserNameOrOccupationBy(
                    specialistInfo?.fullName,
                    'occupation',
                  ),
                }}
                avatarUrl={specialistInfo?.avatar && specialistInfo?.avatar}
                onSignOut={handleSignOut}
              />
            </S.Footer>
          </S.MobileMenu>

          <S.CloseButton onClick={handleCloseMenu}>
            <Close />
          </S.CloseButton>
        </S.MenuDrawer>
      )}
    </S.Container>
  );
};
export default Menu;
